import { action, observable, computed, makeObservable } from 'mobx';
import { apiService } from '../services/api.service';
import { utilsService } from '../services/utils.service';
import { modalsStore } from '../../core/stores/modals.store';
import { history } from 'fn-common-ui';

import _ from 'lodash';

export default class AppDetailStore {

	constructor(masterStore) {
		makeObservable(this);
		this.master = masterStore;
		this.averageStars = (reviews) => {
			if (reviews.length === 0) {
				return 0;
			} else {
				return reviews.reduce((a, b) => a + b.reviewStar, 0) / reviews.length;
			}
		};

		this.createAlert = masterStore.createAlert.bind(masterStore);
	}

	getVersionProp(prop) {
		if (this.appVersions.versions) {
			return this.appVersions.versions[this.selectedVersion.versionIndex || 0][prop];
		}
		return '';
	}

	getVersionNumString() {
		const versionNum = this.getVersionProp('version_num');
		if (versionNum) {
			return ` v${versionNum}`;
		}
		return '';
	}

	// ACTIONS

	@action copyApp() {
		let versionNum = this.selectedVersion.versionNum;
		let version = this.appVersions.versions[this.selectedVersion.versionIndex];
		let configObj = {
			apperianId: (this.isReleased ? this.appVersions.app_psk : ''),
			saveId: ((version.saveId !='') ? version.saveId : ''),
			version: versionNum
		};
		const success = (res) => {
			// use the generageAppPageURI function with the new saveId
			history.push(utilsService.generateAppPageURI({saveId: res.data.saveId}, 'copy'));
			this.master.newAppMasterStore.newAppNamePlatformStore.editSavedNameAndPlatform();
		};

		const fail = (err) => {
			let re = /User \d+ Cannot have more than (\d+) drafts/;
			if (re.test(err.response.data.message)) {
				const match = re.exec(err.response.data.message);
				this.createAlert({
					title: 'Draft Limit Reached',
					message: `You have reached the limit of ${match[1]} drafts. Please delete an existing draft before creating another one.`,
					type: 'info'
				});
				window.scrollTo(0, 0);
			} else {
				throw err;
			}
		};

		return apiService.copyApp(configObj).then(success, fail);
	}

	@action getAppVersions(applicationId, versionNumber) {
		const success = (res) => {
			this.appVersions = res.data;
			this.reviewsVisible = versionNumber === 'reviews';
			return this.appVersions; // must return something so that the promise doesn't resolve before we're actually done here
		};

		const fail = (err) => {
			throw err;
		};

		return apiService.getAppVersions(applicationId).then(success, fail);
	}

	@action removeApp(reason) {
		let versionNum = this.selectedVersion.versionNum;
		let version = this.appVersions.versions[this.selectedVersion.versionIndex];
		const success = () => {
			modalsStore.toggleModal('removeApp');

			//FPSE 1715 create alert before changing page, since that resets this.appVersions
			this.createAlert({
				title: 'Your app has been removed.',
				message: `<strong>${this.appVersions.app_name}${this.getVersionNumString()} for ${this.appVersions.platform}</strong> has been removed from the FirstNet App Catalog.`,
				type: 'info'
			}, '/my-apps');

			history.push('/my-apps');
		};

		const fail = (err) => {
			throw err;
		};

		return apiService.removeApp(this.appVersions.app_psk, reason, version.saveId).then(success, fail);
	}

	@action resetAppDetailVersions() {
		this.appVersions = {};
		this.reviewsVisible = false;
		this.selectedVersion = {
			versionIndex: null,
			versionNum: null,
			saveId: null
		};
	}

	@action withdrawApp() {
		const saveId = this.getVersionProp('saveId');

		const success = () => {
			let status = this.appInfoProps.custom_metadata.status;
			let message = '';
			if (status === 'UNDER_REVIEW') {
				message = `<strong>${this.appVersions.app_name}${this.getVersionNumString()} for ${this.appVersions.platform}</strong> will not be reviewed by the FirstNet Team. This does not affect any previously approved versions of the app.`;
			}
			if (status === 'APPROVED') {
				message = `<strong>${this.appVersions.app_name}${this.getVersionNumString()} for ${this.appVersions.platform}</strong> will not be released to the FirstNet App Catalog. This will not affect any previously released versions of the app.`;
			}
			modalsStore.toggleModal('withdrawApp');

			this.createAlert({
				title: 'Your submission has been withdrawn.',
				message,
				type: 'info'
			}, '/my-apps');

			history.push('/my-apps');
		};

		const fail = (err) => {
			throw err;
		};

		return apiService.withdrawApp(saveId).then(success, fail);
	}

	// COMPUTEDS
	@computed get appBannerProps() {
		//console.log('appBannerProps start')
		if (_.isEmpty(this.appVersions)) {
			return null;
		} else {
			return {
				avgStars: this.averageStars(this.appVersions.reviews),
				download_count: this.appVersions.download_count,
				numberOfViews: this.appVersions.numberOfViews,
				icon_path: getLatestAppIcon.call(this),
				app_name: this.appVersions.app_name,
				platform: utilsService.renderPlatformName(this.appVersions.platform),
				isInternalApp: this.appVersions.isInternalApp,
				reviews_count: this.appVersions.reviews_count
			};
		}

		function getLatestAppIcon() {
			let iconPath = '';
			let version = this.appVersions.versions[this.selectedVersion.versionIndex];
			let defaultIcon = '/images/placeholder-image.png';

			if (!this.appVersions.versions || !version) {
				iconPath = defaultIcon;
			} else {
				iconPath = version.icon_path;
			}

			return iconPath;
		}

	}

	@computed get appDataIsAvailable() {
		let available = false;
		if (!_.isEmpty(this.appVersions) && this.appBannerProps && this.appInfoProps && this.appReviews) {
			available = true;
		}
		return available;
	}

	@computed get appInfoProps() {
		let obj = {};

		if (_.isEmpty(this.appVersions)) {
			return undefined;
		} else {
			obj = this.appVersions.versions[this.selectedVersion.versionIndex];
		}

		if (obj && !obj.custom_metadata.search_keywords) {
			obj.custom_metadata.search_keywords = [];
		}

		if (obj) {
			obj.app_name = this.appVersions.app_name;
			obj.platform = this.appVersions.platform;
			obj.device = this.appVersions.device;
			obj.install_uri = this.appVersions.install_uri || 'Store Link Unavailable';
		}

		return obj;
	}

	@computed get appReviews() {
		return this.appVersions.reviews;
	}

	@computed get isReleased() {
		if(!this.appVersions.versions) return false;
		const version = this.appVersions.versions[this.selectedVersion.versionIndex];
		const status = version.custom_metadata.status;
		return ['LIVE', 'INACTIVE', 'INACTIVE - Expired Certificate'].indexOf(status) > -1;
	}

	@computed get isUnreleased() {
		if(!this.appVersions.versions) return true;
		const version = this.appVersions.versions[this.selectedVersion.versionIndex];
		const status = version.custom_metadata.status;
		return ['APPROVED', 'REJECTED', 'UNDER_REVIEW', 'WITHDRAWN'].indexOf(status) > -1;
	}

	// OBSERVABLES
	@observable appVersions = {};
	@observable reviewsVisible = false;
	@observable selectedVersion = {
		versionIndex: null,
		versionNum: null,
		saveId: null
	};

}
